import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useSelector } from 'react-redux';

import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloudSyncOutlined,
  CreditCardFilled,
  FileOutlined,
} from '@ant-design/icons';

import { Steps, Typography } from 'antd';

import {
  FormAddButton as ActionButton,
  RibbonButton,
  StyledModal,
  WithLoading,
} from 'common/components';

import {
  ActiveProductCharge,
  ProductsActiveProcessing,
} from './components/make-product-active';

import {
  modalBodyProductActiveHeight,
  modalMakeProductActiveWidth,
} from './components/ixone-credit-card';

import { calculateTotalFee } from 'common/components/product-add/make-product-active/utils';

import selectorsGridView from 'common/components/grid-view/controllers/selectors';

import { useValidateActiveProduct } from './components/make-product-active/hooks';
import { useGetMemberId, useGetProductFullView, useVisibleModal } from 'hooks';
import { useCheckCreditCardPermission } from 'hooks/useCheckCreditCardPermission';
import { useCheckPaymentTermMonthAndMemberStatusCreditReview } from 'hooks/member/useCheckPaymentTermMonthAndMemberStatusCreditReview';
import { useBrokerHasInitiateBillingEvent } from 'hooks/impersonate/useBrokerHasInitiateBillingEvent';

import { toArray } from 'utils/array';
import { disableMakeActiveProduct } from 'utils/ixone-capture';

import { useQueryClient } from '@tanstack/react-query';
import { keys as keyGetBillingProperties } from 'pages/check-credit-card-info/queries';
import { keys as keyGetSavedMemberCard } from 'pages/company-profile/components/tabs/billing/queries';

import './MakeProductActive.less';

const { Step } = Steps;

const MakeProductActive = ({ isFullView = false }) => {
  const [visibleModal, { onModal, offModal }] = useVisibleModal();

  const { id: productId } = useParams();
  const queryClient = useQueryClient();
  const { memberId } = useGetMemberId();

  const { canPayViaCreditCard: hasPermissionActiveProduct } =
    useCheckCreditCardPermission();
  const isPaymentTermMonthAndMemberStatusCreditReview =
    useCheckPaymentTermMonthAndMemberStatusCreditReview();
  const brokerHasInitiateBillingEvent = useBrokerHasInitiateBillingEvent();

  const { productFull } = useGetProductFullView({
    productId,
    enabled: Boolean(productId && isFullView),
  });

  const selectProductGrid = useSelector(
    selectorsGridView.makeSelectDetailCurrentITemsSelection()
  );

  const selectedProductIdsGrid =
    selectProductGrid?.map((product) => parseInt(product?.id)) ?? [];

  const productIds = isFullView ? toArray(productId) : selectedProductIdsGrid;

  const disabledGridView =
    selectProductGrid?.length === 0 ||
    selectProductGrid?.some((product) =>
      Boolean(disableMakeActiveProduct(product))
    );

  const disabledFullView = disableMakeActiveProduct(productFull);

  useEffect(() => {
    if (visibleModal) {
      queryClient.invalidateQueries({
        queryKey: keyGetBillingProperties.getBillingProperties(memberId),
      });
      queryClient.invalidateQueries({
        queryKey: keyGetSavedMemberCard.getSavedMemberCard(memberId),
      });
    }
  }, [visibleModal]);

  return (
    <>
      <RibbonButton
        disabled={
          isFullView
            ? !hasPermissionActiveProduct ||
              isPaymentTermMonthAndMemberStatusCreditReview ||
              disabledFullView ||
              !brokerHasInitiateBillingEvent
            : !hasPermissionActiveProduct ||
              isPaymentTermMonthAndMemberStatusCreditReview ||
              disabledGridView ||
              !brokerHasInitiateBillingEvent
        }
        size='small'
        icon={<CloudSyncOutlined />}
        label={'Make Product Active'}
        onClick={onModal}
      />
      <MakeProductActiveStep
        productIds={productIds}
        offModal={offModal}
        visibleModal={visibleModal}
        isFullView={isFullView}
      />
    </>
  );
};

const MakeProductActiveStep = ({
  productIds,
  offModal,
  visibleModal,
  isFullView,
  ...otherProps
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const { isLoading, passedProducts } = useValidateActiveProduct({
    productIds,
    enabled: visibleModal,
  });

  const totalFee = calculateTotalFee(passedProducts);

  const handleCancelModal = () => {
    offModal();
    setCurrentStep(0);
  };

  const modalProps = {
    title: 'Make Product Active',
    wrapClassName: 'make-product-active__modal-step',
    visible: visibleModal,
    onCancel: handleCancelModal,
    width: modalMakeProductActiveWidth,
    bodyStyle: {
      overflow: 'hidden',
      marginBottom: 6,
      ...modalBodyProductActiveHeight,
    },
    maskClosable: false,
    footer: null,
    destroyOnClose: true,
    ...otherProps,
  };

  return (
    <>
      {visibleModal && (
        <StyledModal {...modalProps}>
          <WithLoading loading={isLoading}>
            <Steps
              current={currentStep}
              style={{
                width: '80%',
                margin: 'auto',
                marginTop: 12,
              }}
            >
              <Step
                key={0}
                title={
                  <Typography.Text strong style={{ fontSize: 16 }}>
                    Update Requirements
                  </Typography.Text>
                }
              />
              <Step
                key={1}
                title={
                  <Typography.Text strong style={{ fontSize: 16 }}>
                    {totalFee ? 'Charge' : 'Preview'}
                  </Typography.Text>
                }
                icon={totalFee ? <CreditCardFilled /> : <FileOutlined />}
              />
            </Steps>

            <div
              style={{ paddingTop: 6 }}
              className='make-product-active__section'
            >
              {currentStep === 0 ? (
                <ProductsActiveProcessing
                  productIds={productIds}
                  mode={isFullView ? 'edit' : 'add'}
                >
                  <ActionButton
                    text='Next'
                    icon={<ArrowRightOutlined />}
                    onClick={() => setCurrentStep(currentStep + 1)}
                  />
                </ProductsActiveProcessing>
              ) : (
                <ActiveProductCharge
                  onCancel={handleCancelModal}
                  productIds={productIds}
                  isFullView={isFullView}
                >
                  <ActionButton
                    text='Back'
                    icon={<ArrowLeftOutlined />}
                    type='default'
                    onClick={() => setCurrentStep(currentStep - 1)}
                  />
                </ActiveProductCharge>
              )}
            </div>
          </WithLoading>
        </StyledModal>
      )}
    </>
  );
};

export default MakeProductActive;
